import React, { useState, useEffect, useCallback, useRef } from 'react';
//import { CircularProgressCentered } from "../../app/components/sharedcomponents";
import {
    Box,
    Typography,
    Button,
    TextField,
    Link,
    Grid,
    Paper,
    MenuItem,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useForm, Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CountrySelect } from '../../app/components/CountrySelect';
import type { CountryType } from '../../app/components/CountryCodes'; // Import CountryType
import { countries } from '../../app/components/CountryCodes'; // Import countries array
import { useAddNonUserProfileMutation } from '../../features/profilesSlice';
import { useAuthProfile } from '../../app/hooks';

interface FormValues {
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    country: string;
    birthmonth: string;
    birthday: string;
    birthyear: string;
    facility_name: string;
    treating_doctor_s_name: string;
    consent_to_terms: boolean;
    date_of_birth: string; // Add date_of_birth for RHF management
}

const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const days = Array.from({ length: 31 }, (_, i) => `${i + 1}`);
const years = Array.from({ length: 100 }, (_, i) => `${new Date().getFullYear() - i}`);

// Find the default country (e.g., United States)
export const defaultCountry = countries.find(c => c.code === 'US') || countries[0]; // Fallback to the first country if US not found

export const ClinicalTrialsSignupForm: React.FC<{ setSubmitted: () => void, scrollUp: () => void }> = ({ scrollUp, setSubmitted }) => {
    const { authStatus, authUser, profile, isLoading, isSuccess: isSuccessAuth, isError: isErrorAuth, error, /*isSuccessMessaging: isSuccessMessaging*/ } = useAuthProfile()
    const { control, handleSubmit, formState: { errors, isValid }, setValue, trigger, watch } = useForm<FormValues>({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: {
            firstname: '',
            lastname: '',
            email: isSuccessAuth && authStatus === 'authenticated' ? profile?.email : '',
            phone: defaultCountry.phone, // Set default phone prefix
            country: defaultCountry.phone,
            birthmonth: '',
            birthday: '',
            birthyear: '',
            facility_name: '',
            treating_doctor_s_name: '',
            consent_to_terms: false,
            date_of_birth: '', // Add default value
        }
    });
    const formRef = useRef<HTMLFormElement>(null);
    const [isHubSpotLoaded, setIsHubSpotLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hubspotError, setHubspotError] = useState<string | null>(null);
    const navigate = useNavigate();

    // State for the selected country object - Initialize with default
    const [selectedCountry, setSelectedCountry] = useState<CountryType | null>(defaultCountry);

    // --- Get mutation trigger and status flags ---
    const [addNonUserProfile, { data: dataAddNonUserProfile, isLoading: isAddingProfile, isSuccess, isError, error: addProfileError, reset: resetMutation }] = useAddNonUserProfileMutation();

    useEffect(() => {
        const script = document.createElement('script');
        script.src = '//js.hs-scripts.com/2678056.js'; // Replace with your HubSpot ID
        script.async = true;
        script.defer = true;
        script.id = 'hs-script-loader';

        script.onload = () => {
            console.log('HubSpot script loaded successfully.');
            setIsHubSpotLoaded(true);
        };

        script.onerror = () => {
            console.error('Failed to load HubSpot script.');
            setIsHubSpotLoaded(false);
            setHubspotError("Failed to load HubSpot tracking script. Please check your connection or ad-blocker.");
        };

        document.head.appendChild(script);

        // Cleanup function to remove the script when the component unmounts
        return () => {
            const existingScript = document.getElementById('hs-script-loader');
            if (existingScript && existingScript.parentNode) {
                console.log('Removing HubSpot script.');
                existingScript.parentNode.removeChild(existingScript);
            }
        };
    }, []); // Empty dependency array ensures this runs only once on mount

    // Watch the individual date fields for the hidden input
    const birthMonth = watch('birthmonth');
    const birthDay = watch('birthday');
    const birthYear = watch('birthyear');

    // Effect to update the formatted birthdate for the hidden field
    useEffect(() => {
        if (birthMonth && birthDay && birthYear) {
            const monthIndex = months.indexOf(birthMonth);
            if (monthIndex !== -1) {
                const monthNumber = String(monthIndex + 1).padStart(2, '0'); // Ensure MM format (01-12)
                const dayPadded = String(birthDay).padStart(2, '0');        // Ensure DD format (01-31)
                const yearString = String(birthYear);
                // Basic check for 4-digit year (your validation might be more robust)
                if (yearString.length === 4) {
                    setValue('date_of_birth', `${monthNumber}/${dayPadded}/${yearString}`);
                } else {
                    setValue('date_of_birth', '');
                }
            } else {
                setValue('date_of_birth', '');
            }
        } else {
            setValue('date_of_birth', '');
        }
    }, [birthMonth, birthDay, birthYear, months, setValue]); // Add setValue to dependencies

    const handleCustomButtonClick = async () => {
        // 1. Manually trigger validation for the entire form
        const result = await trigger(); // You can also pass specific field names: trigger("firstName") or trigger(["firstName", "email"])

        if (result) {
            console.log("Validation passed! Triggering actual submit...");
            if (formRef.current) {
                formRef.current.requestSubmit();
            } else {
                console.error("Form reference not found. Native form submission might fail.");
                setIsSubmitting(false);
                setHubspotError("Internal error: Form element not found.");
            }
        } else {
            console.log("Validation failed. Please correct errors.");
            // Optionally, you might want to scroll to the first error or give other feedback.
        }
    };


    const onSubmit = useCallback(async (data: FormValues) => {
        setIsSubmitting(true);
        setHubspotError(null);

        try { // This try block now only covers pre-mutation logic
            const month = String(months.indexOf(data.birthmonth) + 1).padStart(2, '0');
            const day = String(data.birthday).padStart(2, '0');
            const birthdate = `${data.birthyear}-${month}-${day}`;

            const formData = new FormData(); // Keep FormData for logging or potential direct use
            formData.append('firstname', data.firstname);
            formData.append('lastname', data.lastname);
            formData.append('email', data.email);
            formData.append('phone', data.phone);
            formData.append('country', data.country);
            formData.append('birthdate', birthdate);
            formData.append('facility_name', data.facility_name);
            formData.append('treating_doctor_s_name', data.treating_doctor_s_name);
            formData.append('consent_to_terms', data.consent_to_terms.toString());
            formData.append('date_of_birth', data.date_of_birth); // Use value from RHF

            for (const pair of (formData as any).entries()) {
                console.log(pair[0] + ', ' + pair[1]);
            }

            try {
                addNonUserProfile({ profilePTO: { email: data.email, metadata: { massiveBioData: data } } });
                // Optionally log that the trigger was called
                console.log("Triggered addNonUserProfile mutation");
            } catch (apiError) {
                // This catch is unlikely to trigger for standard RTK Query async errors,
                // but might catch immediate synchronous errors in the hook call itself.
                console.error('Error directly triggering addNonUserProfile:', apiError);
                // Consider setting an error state here if this case is possible/important
            }

            // --- HubSpot submission logic remains ---
            // The form will still submit implicitly, allowing HubSpot to capture it.

            // --- Start timer immediately after triggering mutation ---
            setTimeout(() => {
                setIsSubmitting(false); // Set loading false after delay
                setSubmitted();         // Set submitted state after delay
            }, 3000); // Original delay

        } catch (error: any) { // Catches errors in data preparation
            console.error('Error preparing form data:', error);
            setIsSubmitting(false); // Ensure loading stops if prep fails
            setHubspotError(`An error occurred during form preparation: ${error.message || 'Unknown error'}`);
        }
    }, [isHubSpotLoaded, months, addNonUserProfile]); // Removed setSubmitted, Added addNonUserProfile

    // --- useEffect for Mutation Success ---
    useEffect(() => {
        if (isSuccess)
            console.log("Massive Bio submission succeeded");
        if (isError)
            console.log("Massive Bio submission may have failed");
        //scrollUp()
    }, [isSuccess]); // Add deps



    return (
        <Paper elevation={1} sx={{ px: { xs: 2, sm: 3, md: 5, lg: 5, xl: 10 }, borderRadius: 2, py: 3, mb: 4, mt: 2 }}>
            <Box sx={{ px: 1 }}>
                <Typography variant="h5" align="center" fontWeight={700} mb={3} mt={-3}>
                    Let&apos;s Explore Your Options
                </Typography>

                <Box component="form" ref={formRef} onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Grid container spacing={2}>
                        <Grid sx={{
                            width: {
                                xs: '100%',   // full width on extra-small screens
                                sm: '50%',    // half width on small screens and up
                            },
                        }}>
                            <Controller
                                name="firstname"
                                control={control}
                                rules={{ required: 'First name is required' }}
                                render={({ field }) => (
                                    <TextField {...field} label={<RequiredLabel label="First Name" />} placeholder="e.g. John Michael" fullWidth size="small" error={!!errors.firstname} helperText={errors.firstname?.message} />
                                )}
                            />
                        </Grid>
                        <Grid sx={{
                            width: {
                                xs: '100%',   // full width on extra-small screens
                                sm: '50%',    // half width on small screens and up
                            },
                        }}>
                            <Controller
                                name="lastname"
                                control={control}
                                rules={{ required: 'Last name is required' }}
                                render={({ field }) => (
                                    <TextField {...field} label={<RequiredLabel label="Last Name" />} placeholder="e.g. Doe" fullWidth size="small" error={!!errors.lastname} helperText={errors.lastname?.message} />
                                )}
                            />
                        </Grid>

                        <Grid sx={{ width: '100%' }}>
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: 'Email is required',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'Invalid email address'
                                    }
                                }}
                                render={({ field }) => (
                                    <TextField {...field} label={<RequiredLabel label="Your Email Address" />} placeholder="e.g. john@example.com" fullWidth size="small" error={!!errors.email} helperText={errors.email?.message} />
                                )}
                            />
                        </Grid>


                        <Grid sx={{
                            width: {
                                xs: '100%',       // xs=12 → 12/12 = 100%
                                sm: '100%',       // sm=12 → 12/12 = 100%
                                md: '58.333%',    // md=7 → 7/12 ≈ 58.333%
                                lg: '50%',        // lg=6 → 6/12 = 50%
                            },
                        }}>
                            <Controller
                                name="country"
                                control={control}
                                rules={{ required: 'Country Code is required' }}
                                render={({ field }) => (
                                    <Box sx={{ maxWidth: '100%', width: '100%' }}>
                                        <CountrySelect
                                            value={selectedCountry}
                                            // Change displayField to 'label'
                                            displayField="label"
                                            onChange={(event, value, reason, details) => {
                                                setSelectedCountry(value ?? null);
                                                const phonePrefix = value ? value.phone : ''; // Get prefix like +1
                                                setValue('country', phonePrefix); // Update country field (contains prefix)
                                                field.onChange(phonePrefix); // Inform react-hook-form about country change
                                                setValue('phone', phonePrefix); // Update phone field to new prefix
                                                trigger('country'); // Trigger validation if needed
                                            }}
                                            error={!!errors.country}
                                            helperText={errors.country?.message}
                                            required // Pass the required prop
                                        />
                                    </Box>
                                )}
                            />
                        </Grid>
                        <Grid sx={{
                            width: {
                                xs: '100%',   // xs=12 → 12/12 = 100%
                                sm: '100%',   // sm=12 → 12/12 = 100%
                                md: '41.666%', // md=5 → 5/12 ≈ 41.666%
                                lg: '50%',     // lg=6 → 6/12 = 50%
                            },
                        }}>
                            <Controller
                                name="phone"
                                control={control}
                                rules={{
                                    required: 'Phone number is required', minLength: {
                                        value: 10,
                                        message: "Phone number must be at least 10 digits",
                                    },
                                    // Optional: Add a pattern to ensure it's digits only, or other formatting
                                    pattern: {
                                        value: /^[0-9]+$/, // Allows only digits
                                        message: "Phone number must contain only digits",
                                    },
                                }}
                                render={({ field }) => {
                                    // Get the current country prefix directly from the selectedCountry state
                                    const currentPrefix = selectedCountry?.phone || '';
                                    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                                        const currentValue = event.target.value;

                                        if (!currentPrefix || currentValue.length < currentPrefix.length || !currentValue.startsWith(currentPrefix)) {
                                            field.onChange(currentPrefix);
                                            return;
                                        }
                                        const digitsPart = currentValue.substring(currentPrefix.length).replace(/\D/g, '');
                                        const truncatedDigitsPart = digitsPart.slice(0, 10);
                                        field.onChange(currentPrefix + truncatedDigitsPart);
                                    };

                                    return (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label={<RequiredLabel label="Your Phone Number" />}
                                            placeholder="e.g., +1 555 123 4567"
                                            onChange={handlePhoneChange}
                                            inputProps={{
                                                inputMode: 'tel',
                                                pattern: '[0-9]*'
                                            }}
                                            size="small"
                                            error={!!errors.phone}
                                            helperText={errors.phone?.message}
                                            sx={{ flexGrow: 1 }}
                                        />
                                    );
                                }}
                            />
                        </Grid>

                        <Grid sx={{ width: '100%', py: 0 }}>
                            <Typography fontSize={14} sx={{ py: 0, mb: -1.5, mt: -1 }}><RequiredLabel label="Your Birthday" /></Typography>
                        </Grid>

                        <Grid sx={{
                            width: {
                                xs: '100%',   // full width on extra-small screens
                                sm: '33.333%', // 4/12 = 33.33% on small screens and up
                            },
                        }}>
                            <Controller
                                name="birthmonth"
                                control={control}
                                rules={{ required: 'Month is required' }}
                                render={({ field }) => (
                                    <TextField {...field} label={<RequiredLabel label="Month" />} select fullWidth size="small" error={!!errors.birthmonth} helperText={errors.birthmonth?.message}>
                                        {months.map((m) => (
                                            <MenuItem key={m} value={m}>{m}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid sx={{
                            width: {
                                xs: '100%',   // full width on extra-small screens
                                sm: '33.333%', // 4/12 = 33.33% on small screens and up
                            },
                        }}>
                            <Controller
                                name="birthday"
                                control={control}
                                rules={{ required: 'Day is required' }}
                                render={({ field }) => (
                                    <TextField {...field} label={<RequiredLabel label="Day" />} select fullWidth size="small" error={!!errors.birthday} helperText={errors.birthday?.message}>
                                        {days.map((d) => (
                                            <MenuItem key={d} value={d}>{d}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid sx={{
                            width: {
                                xs: '100%',   // full width on extra-small screens
                                sm: '33.333%', // 4/12 = 33.33% on small screens and up
                            },
                        }}>
                            <Controller
                                name="birthyear"
                                control={control}
                                rules={{ required: 'Year is required' }}
                                render={({ field }) => (
                                    <TextField {...field} label={<RequiredLabel label="Year" />} select fullWidth size="small" error={!!errors.birthyear} helperText={errors.birthyear?.message}>
                                        {years.map((y) => (
                                            <MenuItem key={y} value={y}>{y}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        {/* Non Visible, Read-Only field for HubSpot */}
                        <Grid sx={{ width: '100%', display: { xs: 'none', md: 'none' } }}>
                            <Controller
                                name="date_of_birth"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Date of Birth (MM/DD/YYYY)" // Label for clarity
                                        fullWidth
                                        size="small"
                                        // Make it non-editable by the user
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        disabled // Visually indicate non-editable state
                                    // No error display needed as it's derived
                                    />
                                )}
                            />
                        </Grid>

                        <Grid sx={{ width: '100%' }}>
                            <Controller
                                name="facility_name"
                                control={control}
                                render={({ field }) => (
                                    <TextField {...field} label="Facility Where You Receive Treatment" placeholder="e.g. ABC Hospital" fullWidth size="small" error={!!errors.facility_name} helperText={errors.facility_name?.message} />
                                )}
                            />
                        </Grid>

                        <Grid sx={{ width: '100%' }}>
                            <Controller
                                name="treating_doctor_s_name"
                                control={control}
                                render={({ field }) => (
                                    <TextField {...field} label="Your Treating Physician" placeholder="e.g. Emily Carter" fullWidth size="small" error={!!errors.treating_doctor_s_name} helperText={errors.treating_doctor_s_name?.message} />
                                )}
                            />
                        </Grid>

                        <Grid sx={{ width: '100%' }}>
                            <Controller
                                name="consent_to_terms"
                                control={control}
                                rules={{ required: 'You must agree to the terms and conditions' }}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Checkbox {...field} checked={field.value} />}
                                        label={
                                            <Typography variant="body2">
                                                I agree to the Massive Bio{' '}
                                                <Link component="button" onClick={() => navigate('/massivebio-privacy-policy')} color="primary">
                                                    privacy policy
                                                </Link>,{' '}
                                                <Link component="button" onClick={() => navigate('/massivebio-terms-and-conditions')} color="primary">
                                                    terms and conditions
                                                </Link>
                                                {' and '}
                                                <Link component="button" onClick={() => navigate('/massivebio-hipaa')} color="primary">
                                                    HIPAA notice
                                                </Link>
                                                <RequiredLabel label='' />
                                            </Typography>
                                        }
                                    />
                                )}
                            />
                            {errors.consent_to_terms && (
                                <Typography color="error" variant="caption">
                                    {errors.consent_to_terms.message}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>

                    {/* <Typography variant="caption" mt={1} display="block">
                        By filling out this form, you&apos;re consenting only to release your medical records. You&apos;re not agreeing to participate in clinical trials yet.
                    </Typography> */}

                    <Box textAlign="center" mt={3}>
                        <Button
                            type="button"
                            variant="contained"
                            onClick={handleCustomButtonClick}
                            sx={{
                                borderRadius: 5,
                                px: 4,
                                py: 1,
                                backgroundColor: '#615af4',
                                '&:hover': { backgroundColor: '#4a45c5' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 180,
                            }}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <CircularProgress size={20} color="info" sx={{ mr: 1 }} />
                                    Loading...
                                </>
                            ) : (
                                'Start Your Journey'
                            )}
                        </Button>
                        {hubspotError && (
                            <Typography style={{ marginTop: '1rem', color: 'red', textAlign: 'center' }}>
                                {hubspotError}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

interface RequiredLabelProps {
    label: string;
}

export const RequiredLabel: React.FC<RequiredLabelProps> = ({ label }) => {
    return (
        <Typography component="label">
            {label}
            <Typography component="span" sx={{ color: 'red', ml: '0.2em' }}>
                *
            </Typography>
        </Typography>
    );
};